import { CommonModule, formatDate } from '@angular/common';
import {
  Component,
  inject,
  OnDestroy,
  output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import {
  CancellationDocumentType,
  getCancellationDocumentTypeLabel,
  isCancellationLetterType,
  SELECTABLE_CANCELLATION_DOCUMENT_TYPES,
} from 'src/app/_enums/cancellation/cancellation-document-type.enum';
import { getApiErrorMessage } from 'src/app/_helpers/api-error.helpers';
import { pdfFile } from 'src/app/_helpers/custom.validators';
import { CancellationService } from 'src/app/_services/cancellation.service';
import { ProposalService } from 'src/app/_services/proposal.service';
import { CancellationFlowDetailsResponse } from 'src/app/_types/cancellation.types';
import { ActionModalComponent } from 'src/app/shared/components/action-modal/action-modal.component';
import { FileInputComponent } from 'src/app/shared/components/file-input/file-input.component';
import { IconsModule } from 'src/app/shared/icons/icons.module';

/** Not a cancellation's: a document of the policy itself, filed on its proposal. */
export const PROPOSAL_POLICY_DOCUMENT = 'POLICY' as const;

type DocumentKind = CancellationDocumentType | typeof PROPOSAL_POLICY_DOCUMENT;

/**
 * « Ajouter un document »: files what an operator holds against a running request — the insurer's
 * reply, a proof of postage, a letter scanned from paper — or, on an affaire, a document of its
 * policy. The type picked decides where the file goes.
 *
 * <p>Filing only. The steps that move a request are the step buttons', and this one never touches
 * the status. A cancellation document is the back office's, and the customer is not shown it; a
 * policy document joins the policy's, which the customer sees.
 */
@Component({
  selector: 'app-cancellation-document-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ActionModalComponent,
    FileInputComponent,
    IconsModule,
  ],
  templateUrl: './cancellation-document-modal.component.html',
  // The dialogs of the communication module, which this is one of: the rules are
  // namespaced on `.compose-modal` / `.compose-form` rather than scoped, so they
  // reach the shell's own footer — hence no encapsulation, as there.
  styleUrls: [
    '../../../Dashboard/customer/communications/compose/compose-modal.css',
  ],
  encapsulation: ViewEncapsulation.None,
})
export class CancellationDocumentModalComponent implements OnDestroy {
  private readonly cancellationService = inject(CancellationService);

  private readonly proposalService = inject(ProposalService);

  private readonly toastr = inject(ToastrService);

  /** The request the document landed on: the refreshed one. */
  readonly changed = output<CancellationFlowDetailsResponse>();

  /** A policy document was filed on the proposal; the host re-reads it. */
  readonly policyAdded = output<void>();

  @ViewChild('actionModal')
  private actionModal?: ActionModalComponent;

  private cancellationId: string | null = null;

  private proposalId: string | null = null;

  private readonly subscriptions = new Subscription();

  /** Only those with somewhere to go: see `openModal`. */
  protected types: DocumentKind[] = [];

  protected readonly form = new FormGroup({
    // Empty rather than null: the placeholder is an <option> like any other, and
    // required refuses it.
    type: new FormControl<DocumentKind | ''>('', {
      nonNullable: true,
      validators: Validators.required,
    }),
    file: new FormControl<File | null>(null, [Validators.required, pdfFile]),
    letterDate: new FormControl('', { nonNullable: true }),
  });

  protected submitted = false;

  constructor() {
    this.subscriptions.add(
      this.form.controls.type.valueChanges.subscribe(() =>
        this.applyTypeValidators()
      )
    );
  }

  /** Nothing is filed before a type is picked, so nothing is a letter until then either. */
  protected get isLetter(): boolean {
    const { value } = this.form.controls.type;
    return (
      value !== PROPOSAL_POLICY_DOCUMENT && isCancellationLetterType(value)
    );
  }

  protected typeLabel(type: DocumentKind): string {
    return type === PROPOSAL_POLICY_DOCUMENT
      ? $localize`Document de police`
      : getCancellationDocumentTypeLabel(type);
  }

  protected get canSubmit(): boolean {
    return !this.submitted && this.form.valid;
  }

  /**
   * Offers the policy type when given the proposal to file it on, and the
   * cancellation types when given the running request.
   */
  openModal(
    cancellation: CancellationFlowDetailsResponse | null,
    policyProposalId: string | null = null
  ): void {
    this.cancellationId = cancellation?.id ?? null;
    this.proposalId = policyProposalId;
    this.types = [
      ...(policyProposalId ? [PROPOSAL_POLICY_DOCUMENT] : []),
      ...(cancellation ? SELECTABLE_CANCELLATION_DOCUMENT_TYPES : []),
    ];
    this.submitted = false;
    this.form.reset({
      type: '',
      file: null,
      letterDate: formatDate(new Date(), 'yyyy-MM-dd', 'en'),
    });
    this.applyTypeValidators();
    this.actionModal?.openModal();
  }

  /** The date is asked for only while what is being filed bears one. */
  private applyTypeValidators(): void {
    const { letterDate } = this.form.controls;
    if (this.isLetter) {
      letterDate.setValidators(Validators.required);
    } else {
      letterDate.clearValidators();
    }
    letterDate.updateValueAndValidity({ emitEvent: false });
  }

  protected confirm(): void {
    const { type, file, letterDate } = this.form.getRawValue();
    if (!type || !file || !this.canSubmit) return;
    if (type === PROPOSAL_POLICY_DOCUMENT) {
      const { proposalId } = this;
      if (!proposalId) return;
      this.submitted = true;
      this.proposalService
        .addPolicyDocument(proposalId, file)
        .subscribe(this.done(() => this.policyAdded.emit()));
      return;
    }
    const id = this.cancellationId;
    if (!id) return;
    this.submitted = true;
    this.cancellationService
      .addDocument(id, {
        file,
        type,
        letterDate: this.isLetter ? letterDate : undefined,
      })
      .subscribe(this.done((res) => this.changed.emit(res.data)));
  }

  private done<T>(filed: (res: T) => void) {
    return {
      next: (res: T) => {
        this.submitted = false;
        this.toastr.success($localize`Document ajouté`);
        filed(res);
        this.actionModal?.closeModal();
      },
      error: (err: unknown) => {
        this.submitted = false;
        this.toastr.error(
          getApiErrorMessage(
            err,
            $localize`Une erreur est survenue. Veuillez réessayer`
          )
        );
      },
    };
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
